import { Component, Input, OnInit } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { NgbActiveModal, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { HttpEventType } from "@angular/common/http";
import { catchError } from "rxjs/operators";
import { of } from "rxjs";
import Swal from "sweetalert2";

import {
  SeguimientoQueja,
  SeguimientoQuejaPayload,
  SeguimientoQuejasService,
} from "../../../../../../../shared/services/seguimiento-quejas.service";
import { Queja, QuejasService } from "../../../../../../../shared/services/quejas.service";
import { DigitalesService } from "../../../../../../../shared/services/digitales.service";
import { AdministracionService } from "../../../../../../../shared/services/administracion.services";

// ==========================================================
// DOCUMENTOS DEL SEGUIMIENTO: mismos tipos que en PasosPlanComponent
// ==========================================================
interface DocumentoAdjunto {
  path: string;
  name: string;
  url: string;
  isImage: boolean;
}

interface ArchivoPendiente {
  file: File;
  preview: string | null;
}

interface DocumentoPreview {
  name: string;
  path: string;
  url: string;
  urlSegura?: SafeResourceUrl;
  isImage: boolean;
  isPdf: boolean;
  tipo: string;
}

const EXTENSIONES_IMAGEN = ["jpg", "jpeg", "png", "webp"];
const MIME_IMAGEN = ["image/jpeg", "image/png", "image/jpg", "image/webp"];

@Component({
  selector: "app-seguimientos-quejas",
  templateUrl: "./seguimientos-quejas.component.html",
  styleUrls: ["./seguimientos-quejas.component.scss"],
})
export class SeguimientosQuejasComponent implements OnInit {
  @Input() queja!: Queja;

  seguimientoForm!: FormGroup;

  seguimientos: SeguimientoQueja[] = [];
  ultimoAvance = 0;

  guardando = false;
  cargandoListado = false;

  modoEdicion = false;
  idSeguimientoEditando: number | null = null;

  readonly Number = Number;

  // ==========================================================
  // DOCUMENTOS DEL SEGUIMIENTO SELECCIONADO
  // ==========================================================
  nit: string | null = null;
  administraciones: any[] = [];

  seguimientoSeleccionadoDocs: SeguimientoQueja | null = null;
  documentosPlan: DocumentoAdjunto[] = [];
  nuevosArchivosDocs: ArchivoPendiente[] = [];
  cargandoDocumentos = false;
  subiendoDocumentos = false;

  /** Documento actualmente previsualizado dentro del modal de documentos. */
  documentoSeleccionado: DocumentoPreview | null = null;
  /** Controla si el modal de documentos muestra la lista o la previsualización. */
  mostrandoPreviewDocumento = false;

  constructor(
    private fb: FormBuilder,
    private activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private sanitizer: DomSanitizer,
    private seguimientoQuejasService: SeguimientoQuejasService,
    private quejasService: QuejasService,
    private digitalesService: DigitalesService,
    private administracionesServices: AdministracionService,
  ) {}

  ngOnInit(): void {
    this.inicializarFormulario();
    this.cargarSeguimientos();
    this.cargarAdministraciones();
  }

  private inicializarFormulario(): void {
    this.seguimientoForm = this.fb.group({
      fecha_seguimiento: ["", Validators.required],
      observacion: ["", [Validators.required, Validators.maxLength(1000)]],
      avance: [
        0,
        [
          Validators.required,
          Validators.min(0),
          Validators.max(100),
          Validators.pattern(/^\d+$/),
          this.avanceMinimoValidator(),
        ],
      ],
    });
  }

  // El backend rechaza avance <= último seguimiento activo (debe ser estrictamente mayor), excepto al editar ese mismo registro
  private avanceMinimoValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.modoEdicion || control.value === null || control.value === "") return null;
      const avance = Number(control.value);
      if (!Number.isInteger(avance)) return null;
      return avance <= this.ultimoAvance ? { avanceMenor: { minimo: this.ultimoAvance } } : null;
    };
  }

  // ============ LISTADO ============

  cargarSeguimientos(): void {
    if (!this.queja?.id) return;

    this.cargandoListado = true;
    this.seguimientoQuejasService
      .getByQueja(Number(this.queja.id))
      .pipe(catchError(() => of([])))
      .subscribe((response: any) => {
        this.seguimientos = this.normalizarLista(response).sort(
          (a: any, b: any) => (b.id || 0) - (a.id || 0),
        );
        this.ultimoAvance = this.seguimientos.length
          ? Number(this.seguimientos[0].avance) || 0
          : 0;
        this.cargandoListado = false;
        // Reevalúa el control de avance ahora que ultimoAvance ya se conoce
        this.seguimientoForm.get("avance")?.updateValueAndValidity();
      });
  }

  private normalizarLista(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }

  // ============ GUARDAR (CREAR / ACTUALIZAR) ============

  guardar(): void {
    if (this.seguimientoForm.invalid) {
      this.seguimientoForm.markAllAsTouched();
      Swal.fire("Campos obligatorios", "Completa todos los campos requeridos.", "warning");
      return;
    }

    if (this.modoEdicion) {
      this.actualizarSeguimiento();
      return;
    }

    const formValue = this.seguimientoForm.getRawValue();

    const payload: SeguimientoQuejaPayload = {
      id_queja: Number(this.queja.id),
      fecha_seguimiento: formValue.fecha_seguimiento,
      observacion: String(formValue.observacion || "").trim(),
      avance: Number(formValue.avance),
      estado: 1,
    };

    this.guardando = true;

    this.seguimientoQuejasService.create(payload).subscribe({
      next: () => {
        this.guardando = false;
        Swal.fire("Éxito", "Seguimiento guardado correctamente.", "success");
        this.resetearFormulario();
        this.cargarSeguimientos();
        this.refrescarQueja();
      },
      error: (error: any) => {
        this.guardando = false;
        const mensaje = error?.error?.error || "No se pudo guardar el seguimiento.";
        Swal.fire("Error", mensaje, "error");
      },
    });
  }

  // El backend puede avanzar el estado_gestion de la queja al crear un seguimiento, se refresca para reflejarlo en el resumen
  private refrescarQueja(): void {
    if (!this.queja?.id) return;
    this.quejasService.getById(Number(this.queja.id)).subscribe((response: any) => {
      const data = response?.data ?? response;
      if (data) this.queja = { ...this.queja, ...data };
    });
  }

  private actualizarSeguimiento(): void {
    if (!this.idSeguimientoEditando) return;

    const formValue = this.seguimientoForm.getRawValue();

    const payload: Partial<SeguimientoQuejaPayload> = {
      fecha_seguimiento: formValue.fecha_seguimiento,
      observacion: String(formValue.observacion || "").trim(),
      avance: Number(formValue.avance),
    };

    this.guardando = true;

    this.seguimientoQuejasService.update(this.idSeguimientoEditando, payload).subscribe({
      next: () => {
        this.guardando = false;
        Swal.fire("Éxito", "Seguimiento actualizado correctamente.", "success");
        this.cancelarEdicion();
        this.cargarSeguimientos();
      },
      error: (error: any) => {
        this.guardando = false;
        const mensaje = error?.error?.error || "No se pudo actualizar el seguimiento.";
        Swal.fire("Error", mensaje, "error");
      },
    });
  }

  private resetearFormulario(): void {
    this.seguimientoForm.reset({ fecha_seguimiento: "", observacion: "", avance: 0 });
  }

  // ============ EDITAR ============

  editarSeguimiento(seg: SeguimientoQueja): void {
    this.modoEdicion = true;
    this.idSeguimientoEditando = Number(seg.id);

    this.seguimientoForm.patchValue({
      fecha_seguimiento: seg.fecha_seguimiento,
      observacion: seg.observacion,
      avance: seg.avance,
    });
  }

  cancelarEdicion(): void {
    this.modoEdicion = false;
    this.idSeguimientoEditando = null;
    this.resetearFormulario();
  }

  // ============ CAMBIO DE ESTADO ============

  cambiarEstado(seg: SeguimientoQueja, estado: number): void {
    const id = Number(seg?.id);
    if (!id) return;

    const accion = estado === 1 ? "activar" : "inactivar";
    Swal.fire({
      title: `¿Deseas ${accion} este seguimiento?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sí, ${accion}`,
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;

      const accionServicio =
        estado === 1
          ? this.seguimientoQuejasService.activar(id)
          : this.seguimientoQuejasService.inactivar(id);

      accionServicio.subscribe({
        next: () => {
          Swal.fire("Éxito", `Seguimiento ${accion}do correctamente.`, "success");
          this.cargarSeguimientos();
        },
        error: (error: any) => {
          const mensaje = error?.error?.error || `No se pudo ${accion} el seguimiento.`;
          Swal.fire("Error", mensaje, "error");
        },
      });
    });
  }

  // ==========================================================
  // ================  DOCUMENTOS DEL SEGUIMIENTO  ===========
  // ==========================================================

  private cargarAdministraciones(): void {
    this.administracionesServices.obtenerTodas().subscribe({
      next: (data: any[]) => {
        this.administraciones = data ?? [];
        this.nit = this.administraciones?.[0]?.nit ?? null;
      },
      error: () => {
        this.administraciones = [];
        this.nit = null;
      },
    });
  }

  abrirModalDocumentosSeguimiento(modalRef: any, seguimiento: SeguimientoQueja): void {
    if (!this.nit) {
      Swal.fire({
        icon: "warning",
        title: "Atención",
        text: "Aún se está cargando la información de la administración. Intente de nuevo en un momento.",
      });
      return;
    }

    this.seguimientoSeleccionadoDocs = seguimiento;
    this.nuevosArchivosDocs = [];
    this.documentosPlan = [];
    this.mostrandoPreviewDocumento = false;
    this.documentoSeleccionado = null;
    this.cargarDocumentosSeguimiento(seguimiento);

    this.modalService.open(modalRef, { size: "lg", centered: true, backdrop: "static" });
  }

  cargarDocumentosSeguimiento(seguimiento: SeguimientoQueja): void {
    if (!this.nit) return;

    this.cargandoDocumentos = true;
    const rutaCompleta = this.construirRutaSeguimientoQueja(seguimiento);

    this.digitalesService.getDocumentosPlanAccion(rutaCompleta).subscribe({
      next: (resp: any) => {
        this.documentosPlan = (resp.archivos ?? []).map((a: any) => ({
          name: a.name,
          path: a.path,
          url: a.url,
          isImage: this.esImagenPorNombre(a.name),
        }));
        this.cargandoDocumentos = false;
      },
      error: () => {
        this.documentosPlan = [];
        this.cargandoDocumentos = false;
      },
    });
  }

  onFileChangeDocumento(event: any): void {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.nuevosArchivosDocs.push({
          file,
          preview: this.isImageFile(file) ? e.target.result : null,
        });
      };
      reader.readAsDataURL(file);
    });

    event.target.value = "";
  }

  quitarArchivoNuevo(index: number): void {
    this.nuevosArchivosDocs.splice(index, 1);
  }

  subirDocumentosSeguimiento(): void {
    const seguimiento = this.seguimientoSeleccionadoDocs;
    if (!seguimiento || Number(seguimiento.estado) === 0 || this.nuevosArchivosDocs.length === 0 || !this.nit) {
      return;
    }

    this.subiendoDocumentos = true;
    const rutaCompleta = this.construirRutaSeguimientoQueja(seguimiento);
    const pendientes = this.nuevosArchivosDocs.length;
    let completados = 0;

    this.nuevosArchivosDocs.forEach((item, index) => {
      const nombreSinExtension = item.file.name.replace(/\.[^/.]+$/, "");
      const sufijoUnico = `${Date.now()}_${index}_${Math.floor(Math.random() * 10000)}`;
      const nombreArchivo = `${nombreSinExtension}_${sufijoUnico}`;

      this.digitalesService.subirDocumentoPlanAccion(item.file, rutaCompleta, nombreArchivo, "SGA").subscribe({
        next: (event) => {
          if (event.type === HttpEventType.Response) {
            completados++;
            if (completados === pendientes) {
              this.subiendoDocumentos = false;
              this.nuevosArchivosDocs = [];
              this.cargarDocumentosSeguimiento(seguimiento);
              Swal.fire({ icon: "success", title: "¡Éxito!", text: "Documento(s) subido(s) correctamente." });
            }
          }
        },
        error: () => {
          completados++;
          this.subiendoDocumentos = false;
          if (completados === pendientes) {
            this.cargarDocumentosSeguimiento(seguimiento);
          }
          Swal.fire({ icon: "error", title: "Error", text: `No se pudo subir el archivo ${item.file.name}.` });
        },
      });
    });
  }

  eliminarDocumentoSeguimiento(doc: DocumentoAdjunto): void {
    const seguimiento = this.seguimientoSeleccionadoDocs;
    if (!seguimiento || Number(seguimiento.estado) === 0) {
      Swal.fire({
        icon: "warning",
        title: "Acción no permitida",
        text: "No se puede eliminar documentos de un seguimiento inactivo.",
      });
      return;
    }

    Swal.fire({
      title: "¿Eliminar documento?",
      text: `¿Está seguro de que desea eliminar "${doc.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;

      this.digitalesService.eliminarArchivo(doc.path).subscribe({
        next: () => {
          this.cargarDocumentosSeguimiento(seguimiento);
          Swal.fire({ icon: "success", title: "¡Éxito!", text: "El documento se eliminó correctamente." });
        },
        error: () => {
          Swal.fire({ icon: "error", title: "Error", text: "No se pudo eliminar el documento." });
        },
      });
    });
  }

  reemplazarDocumentoSeguimiento(doc: DocumentoAdjunto): void {
    const seguimiento = this.seguimientoSeleccionadoDocs;
    if (!seguimiento || Number(seguimiento.estado) === 0) {
      Swal.fire({
        icon: "warning",
        title: "Acción no permitida",
        text: "No se puede reemplazar documentos de un seguimiento inactivo.",
      });
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.jpg,.jpeg,.png,.webp";

    input.onchange = (event: any) => {
      const file: File = event.target.files[0];
      if (!file) return;

      Swal.fire({
        title: "¿Reemplazar documento?",
        text: `Se reemplazará "${doc.name}" por "${file.name}".`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, reemplazar",
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (!result.isConfirmed) return;

        this.subiendoDocumentos = true;

        this.digitalesService.eliminarArchivo(doc.path).subscribe({
          next: () => {
            const rutaCompleta = this.construirRutaSeguimientoQueja(seguimiento);
            const indice = this.documentosPlan.findIndex((d) => d.path === doc.path) + 1;
            const nombreArchivo = `${seguimiento.id}_${indice}`;

            this.digitalesService.subirDocumentoPlanAccion(file, rutaCompleta, nombreArchivo, "SGA").subscribe({
              next: (event) => {
                if (event.type === HttpEventType.Response) {
                  this.subiendoDocumentos = false;
                  this.cargarDocumentosSeguimiento(seguimiento);
                  Swal.fire({ icon: "success", title: "¡Éxito!", text: "Documento reemplazado correctamente." });
                }
              },
              error: () => {
                this.subiendoDocumentos = false;
                Swal.fire({ icon: "error", title: "Error", text: "No se pudo subir el nuevo documento." });
              },
            });
          },
          error: () => {
            this.subiendoDocumentos = false;
            Swal.fire({ icon: "error", title: "Error", text: "No se pudo eliminar el documento anterior." });
          },
        });
      });
    };

    input.click();
  }

  verDocumento(doc: DocumentoAdjunto): void {
    if (!doc.url) return;

    const tipo = this.obtenerTipoDocumento(doc.name);

    this.documentoSeleccionado = {
      ...doc,
      isImage: tipo === "imagen",
      isPdf: tipo === "pdf",
      tipo,
      urlSegura: tipo === "pdf" ? this.sanitizer.bypassSecurityTrustResourceUrl(doc.url) : undefined,
    };

    this.mostrandoPreviewDocumento = true;
  }

  cerrarPreviewDocumento(): void {
    this.mostrandoPreviewDocumento = false;
    this.documentoSeleccionado = null;
  }

  // Ruta GCS siguiendo la convención {nit}/pesv/{module}/{entity}/ del proyecto
  private construirRutaSeguimientoQueja(seguimiento: SeguimientoQueja): string {
    const idQueja = this.queja?.id;
    const carpeta = `pesv/quejas/seguimiento/${idQueja}/${seguimiento.id}`;
    return `${this.nit}/${carpeta}`;
  }

  private esImagenPorNombre(nombre: string): boolean {
    const ext = nombre.split(".").pop()?.toLowerCase() ?? "";
    return EXTENSIONES_IMAGEN.includes(ext);
  }

  private isImageFile(file: File): boolean {
    return MIME_IMAGEN.includes(file.type);
  }

  private obtenerTipoDocumento(nombre: string): "imagen" | "pdf" | "word" | "excel" | "otro" {
    const ext = nombre.split(".").pop()?.toLowerCase() ?? "";
    if (EXTENSIONES_IMAGEN.includes(ext)) return "imagen";
    if (ext === "pdf") return "pdf";
    if (["doc", "docx"].includes(ext)) return "word";
    if (["xls", "xlsx"].includes(ext)) return "excel";
    return "otro";
  }

  cerrar(): void {
    this.activeModal.dismiss();
  }
}